import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { loginUser } from '../../services/auth';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../contexts/ToastContext';

const LoginContainer = styled.div`
  max-width: 480px;
  margin: 0 auto;
  padding: 40px;
  background-color: rgba(44, 18, 73, 0.8);
  border-radius: 24px;
  box-shadow: 
    0 20px 40px rgba(0, 0, 0, 0.3), 
    0 0 0 1px rgba(255, 255, 255, 0.1),
    inset 0 0 0 1px rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  position: relative;
  overflow: hidden;
  
  @media (min-width: 992px) {
    max-width: 520px;
    padding: 45px;
  }
`;

const Title = styled.h2`
  font-family: 'Bubblegum Sans', 'Poppins', sans-serif;
  font-size: 2.4rem;
  color: #ff3eb5;
  margin-bottom: 30px;
  text-align: center;
  text-shadow: 0 2px 15px rgba(255, 62, 181, 0.4);
  position: relative;
  
  &::after {
    content: "";
    display: block;
    width: 60px;
    height: 3px;
    background: #ff3eb5;
    margin: 8px auto 0;
    border-radius: 3px;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: relative;
`;

const Label = styled.label`
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.95rem;
  font-weight: 500;
  margin-left: 4px;
  transition: all 0.3s ease;
  text-align: left;
  display: block;
`;

const Input = styled.input`
  padding: 16px 20px;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background-color: rgba(0, 0, 0, 0.15);
  color: #ffffff;
  font-family: 'Poppins', sans-serif;
  font-size: 1rem;
  outline: none;
  transition: all 0.3s ease;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1);
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }

  &:focus {
    border-color: #ff3eb5;
    background-color: rgba(0, 0, 0, 0.25);
    box-shadow: 
      0 0 0 3px rgba(255, 62, 181, 0.2),
      inset 0 2px 4px rgba(0, 0, 0, 0.2);
  }
  
  &:hover:not(:focus) {
    border-color: rgba(255, 255, 255, 0.3);
  }
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  padding: 18px;
  background: #ffaa00;
  color: #ffffff;
  border: none;
  border-radius: 50px;
  font-size: 1.2rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 15px;
  position: relative;
  overflow: hidden;
  box-shadow: 0 8px 20px rgba(255, 170, 0, 0.3);
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 25px rgba(255, 170, 0, 0.4);
  }

  &:active {
    transform: translateY(-1px);
    box-shadow: 0 5px 15px rgba(255, 170, 0, 0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: 0 8px 20px rgba(255, 170, 0, 0.2);
  }
`;

const ErrorMessage = styled.div`
  color: #ff5555;
  background-color: rgba(255, 85, 85, 0.1);
  padding: 15px;
  border-radius: 16px;
  font-size: 0.95rem;
  margin-bottom: 20px;
  border: 1px solid rgba(255, 85, 85, 0.2);
  display: flex;
  align-items: center;
  gap: 10px;
  
  &::before {
    content: "!";
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 24px;
    height: 24px;
    background-color: rgba(255, 85, 85, 0.9);
    color: white;
    border-radius: 50%;
    font-weight: bold;
  }
`;

// Componente per il messaggio di blocco
const BlockMessage = styled.div`
  color: #ffb100;
  background-color: rgba(255, 177, 0, 0.1);
  padding: 15px;
  border-radius: 16px;
  font-size: 0.95rem;
  margin-bottom: 20px;
  border: 1px solid rgba(255, 177, 0, 0.2);
  display: flex;
  align-items: center;
  gap: 10px;
  
  &::before {
    content: "⏱";
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 24px;
    height: 24px;
    font-size: 1.1rem;
  }
`;

// Componente per mostrare il timer di attesa
const CountdownTimer = styled.span`
  font-weight: 600;
  color: #ffb100;
`;

const LinkContainer = styled.div`
  margin-top: 25px;
  text-align: center;
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.7);

  a {
    color: #00d9ff;
    text-decoration: none;
    font-weight: 500;
    padding: 2px 6px;
    border-radius: 4px;
    transition: all 0.3s ease;
    position: relative;
    
    &::after {
      content: "";
      position: absolute;
      left: 0;
      bottom: 0;
      width: 100%;
      height: 1px;
      background: #00d9ff;
      transform: scaleX(0);
      transform-origin: left;
      transition: transform 0.3s ease;
    }
    
    &:hover {
      color: #38ffd8;
      background-color: rgba(0, 217, 255, 0.1);
      
      &::after {
        transform: scaleX(1);
      }
    }
  }
`;

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { loginContext } = useAuth();
  const { showError, showWarning } = useToast();
  
  // Stati per gestire i tentativi falliti
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [isBlocked, setIsBlocked] = useState(false);
  const [blockExpiresAt, setBlockExpiresAt] = useState(null);
  const [remainingTime, setRemainingTime] = useState(0);
  
  // Costanti per la configurazione
  const MAX_ATTEMPTS = 3;
  const BLOCK_DURATION = 5 * 60 * 1000; // 5 minuti in millisecondi

  // Controlla se l'utente è bloccato
  useEffect(() => {
    // Carica lo stato di blocco dallo storage
    const loadBlockState = () => {
      const storedEmail = localStorage.getItem('blockedEmail');
      const storedExpiresAt = localStorage.getItem('blockExpiresAt');
      
      if (storedEmail && storedExpiresAt) {
        const expiresAt = parseInt(storedExpiresAt, 10);
        if (Date.now() < expiresAt) {
          // L'utente è ancora bloccato
          setIsBlocked(true);
          setBlockExpiresAt(expiresAt);
        } else {
          // Il blocco è scaduto, pulisci lo storage
          localStorage.removeItem('blockedEmail');
          localStorage.removeItem('blockExpiresAt');
          setIsBlocked(false);
        }
      }
    };
    
    loadBlockState();
  }, []);
  
  // Timer per aggiornare il tempo rimanente
  useEffect(() => {
    if (!isBlocked || !blockExpiresAt) return;
    
    const updateRemainingTime = () => {
      const now = Date.now();
      const timeLeft = Math.max(0, blockExpiresAt - now);
      
      setRemainingTime(timeLeft);
      
      if (timeLeft === 0) {
        setIsBlocked(false);
        localStorage.removeItem('blockedEmail');
        localStorage.removeItem('blockExpiresAt');
      }
    };
    
    // Aggiorna subito il tempo rimanente
    updateRemainingTime();
    
    // Imposta un intervallo per aggiornare il tempo rimanente
    const intervalId = setInterval(updateRemainingTime, 1000);
    
    return () => clearInterval(intervalId);
  }, [isBlocked, blockExpiresAt]);
  
  // Formatta il tempo rimanente in minuti e secondi
  const formatRemainingTime = (ms) => {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };
  
  // Funzione per bloccare l'utente
  const blockUser = (email) => {
    const expiresAt = Date.now() + BLOCK_DURATION;
    setIsBlocked(true);
    setBlockExpiresAt(expiresAt);
    setRemainingTime(BLOCK_DURATION);
    
    // Salva lo stato di blocco nello storage
    localStorage.setItem('blockedEmail', email);
    localStorage.setItem('blockExpiresAt', expiresAt.toString());
    
    // Mostra un toast di avviso
    showWarning(`Account bloccato per troppi tentativi falliti. Riprova tra ${formatRemainingTime(BLOCK_DURATION)}.`);
  };
  
  // Funzione per gestire un tentativo fallito
  const handleFailedAttempt = () => {
    const newAttempts = failedAttempts + 1;
    setFailedAttempts(newAttempts);
    
    if (newAttempts >= MAX_ATTEMPTS) {
      blockUser(email);
    } else {
      showError(`Tentativo fallito (${newAttempts}/${MAX_ATTEMPTS}). Dopo ${MAX_ATTEMPTS} tentativi l'account verrà bloccato temporaneamente.`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!email || !password) {
      setError('Please fill in all fields');
      return;
    }
    
    // Controlla se l'utente è bloccato
    if (isBlocked) {
      setError(`Troppi tentativi di accesso falliti. Riprova tra ${formatRemainingTime(remainingTime)}.`);
      return;
    }
    
    setLoading(true);
    
    try {
      const loggedInUser = await loginUser(email, password);
      
      if (loggedInUser) {
        // Reset dei tentativi falliti dopo un login riuscito
        setFailedAttempts(0);
        await loginContext();
        navigate('/dashboard');
      } else {
        // Incrementa i tentativi falliti se l'API risponde ma non restituisce utente
        handleFailedAttempt();
        setError('Login did not return user data. Please try again.');
      }
    } catch (err) {
      console.error('Login error:', err);
      // Incrementa i tentativi falliti
      handleFailedAttempt();
      
      let errorMessage = 'Failed to log in. Please try again.';
      if (err.response && err.response.data && err.response.data.message) {
        errorMessage = err.response.data.message;
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <LoginContainer>
      <Title>Login to InstaRizz</Title>
      
      {error && <ErrorMessage>{error}</ErrorMessage>}
      
      {isBlocked && (
        <BlockMessage>
          Account temporaneamente bloccato per troppi tentativi falliti. Riprova tra <CountdownTimer>{formatRemainingTime(remainingTime)}</CountdownTimer>.
        </BlockMessage>
      )}
      
      <Form onSubmit={handleSubmit}>
        <FormGroup>
          <Label htmlFor="email">Email</Label>
          <Input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading || isBlocked}
            placeholder="your@email.com"
            required
          />
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="password">Password</Label>
          <Input
            type="password"
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading || isBlocked}
            placeholder="••••••••"
            required
          />
        </FormGroup>
        
        <Button type="submit" disabled={loading || isBlocked}>
          {loading ? 'Logging in...' : 'Login'}
        </Button>
      </Form>
      
      <LinkContainer>
        Don't have an account? <Link to="/register">Sign up</Link>
      </LinkContainer>
    </LoginContainer>
  );
};

export default Login; 